import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, 
  Download, 
  Printer,
  FileText,
  BarChart3,
  Map,
  Users,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportGenerator({ user }) {
  const { surveyId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchReport();
  }, [surveyId]);

  const fetchReport = async () => {
    try {
      const response = await fetch(`${API}/analytics/report/${surveyId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReport(data);
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600';
      case 'negative': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Generating report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Report Unavailable</h2>
            <p className="text-muted-foreground mb-4">Unable to generate report for this survey.</p>
            <Link to="/admin">
              <Button>Return to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="report-generator">
      {/* Header - Hidden on print */}
      <header className="bg-white border-b sticky top-0 z-10 no-print">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              </Link>
              <div className="h-6 w-px bg-border"></div>
              <h1 className="font-semibold">Survey Report</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
              <Button onClick={handleDownloadPDF}>
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Report Content */}
      <main ref={reportRef} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report Header */}
        <div className="text-center mb-12 print-avoid-break">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-primary rounded flex items-center justify-center">
              <span className="text-white font-bold">CV</span>
            </div>
            <span className="text-xl font-bold">Citizen Voice Nigeria</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">{report.survey?.title}</h1>
          <p className="text-muted-foreground mb-4">{report.survey?.description}</p>
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <Badge variant="secondary">{report.survey?.governance_level}</Badge>
            <Badge variant="outline">{report.survey?.policy_category}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Report generated: {formatDate(report.generated_at)}
          </p>
        </div>

        {/* Executive Summary */}
        <Card className="mb-8 print-avoid-break">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Executive Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
                <p className="text-3xl font-bold">{report.summary?.total_responses?.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Total Responses</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Map className="h-8 w-8 mx-auto mb-2 text-primary" />
                <p className="text-3xl font-bold">{report.summary?.states_covered}</p>
                <p className="text-sm text-muted-foreground">States Covered</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 text-primary" />
                <p className="text-3xl font-bold capitalize">
                  {Object.entries(report.summary?.sentiment_distribution || {})
                    .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
                </p>
                <p className="text-sm text-muted-foreground">Dominant Sentiment</p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t">
              <h4 className="font-semibold mb-4">Sentiment Breakdown</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">Positive</span>
                    <span className="text-sm font-medium text-green-600">
                      {report.summary?.sentiment_distribution?.positive || 0}
                    </span>
                  </div>
                  <Progress 
                    value={report.summary?.total_responses > 0 
                      ? (report.summary?.sentiment_distribution?.positive || 0) / report.summary?.total_responses * 100 
                      : 0} 
                    className="h-2 bg-green-100 [&>div]:bg-green-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">Neutral</span>
                    <span className="text-sm font-medium text-gray-600">
                      {report.summary?.sentiment_distribution?.neutral || 0}
                    </span>
                  </div>
                  <Progress 
                    value={report.summary?.total_responses > 0 
                      ? (report.summary?.sentiment_distribution?.neutral || 0) / report.summary?.total_responses * 100 
                      : 0} 
                    className="h-2 bg-gray-100 [&>div]:bg-gray-400"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">Negative</span>
                    <span className="text-sm font-medium text-red-600">
                      {report.summary?.sentiment_distribution?.negative || 0}
                    </span>
                  </div>
                  <Progress 
                    value={report.summary?.total_responses > 0 
                      ? (report.summary?.sentiment_distribution?.negative || 0) / report.summary?.total_responses * 100 
                      : 0} 
                    className="h-2 bg-red-100 [&>div]:bg-red-500"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Concerns */}
        {report.top_concerns?.length > 0 && (
          <Card className="mb-8 print-avoid-break">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Top Citizen Concerns
              </CardTitle>
              <CardDescription>Most selected responses across all questions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {report.top_concerns.slice(0, 5).map((concern, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium">{concern.concern}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={concern.percentage} className="flex-1 h-2" />
                        <span className="text-sm text-muted-foreground w-16 text-right">
                          {concern.percentage}%
                        </span>
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">{concern.count} votes</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* State-by-State Summary */}
        {report.state_summary?.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Map className="h-5 w-5" />
                State-by-State Highlights
              </CardTitle>
              <CardDescription>Breakdown of responses by state</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">State</th>
                      <th className="text-right py-3 px-4">Responses</th>
                      <th className="text-center py-3 px-4">Dominant Sentiment</th>
                      <th className="text-right py-3 px-4">Positive</th>
                      <th className="text-right py-3 px-4">Neutral</th>
                      <th className="text-right py-3 px-4">Negative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.state_summary.map((state) => (
                      <tr key={state.state} className="border-b hover:bg-muted/30">
                        <td className="py-3 px-4 font-medium">{state.state}</td>
                        <td className="py-3 px-4 text-right">{state.responses}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge 
                            variant="outline" 
                            className={getSentimentColor(state.dominant_sentiment)}
                          >
                            {state.dominant_sentiment}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right text-green-600">
                          {state.sentiment_breakdown?.positive || 0}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-600">
                          {state.sentiment_breakdown?.neutral || 0}
                        </td>
                        <td className="py-3 px-4 text-right text-red-600">
                          {state.sentiment_breakdown?.negative || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Question Details */}
        <Card className="mb-8 print-break">
          <CardHeader>
            <CardTitle>Detailed Question Analysis</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {report.survey?.questions?.map((question, qIndex) => {
              const qAnalytics = report.question_analytics?.[question.question_id];
              if (!qAnalytics) return null;

              return (
                <div key={question.question_id} className="print-avoid-break">
                  <div className="flex items-start gap-3 mb-4">
                    <Badge variant="outline">Q{qIndex + 1}</Badge>
                    <div>
                      <h4 className="font-semibold">{question.text}</h4>
                      <p className="text-sm text-muted-foreground capitalize">
                        {question.question_type.replace('_', ' ')}
                      </p>
                    </div>
                  </div>

                  {question.question_type === 'multiple_choice' && qAnalytics.options && (
                    <div className="space-y-2 ml-9">
                      {qAnalytics.options.map((opt) => (
                        <div key={opt.option_id} className="flex items-center gap-4">
                          <span className="flex-1 text-sm">{opt.text}</span>
                          <Progress value={opt.percentage} className="w-32 h-2" />
                          <span className="text-sm text-muted-foreground w-20 text-right">
                            {opt.count} ({opt.percentage}%)
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {question.question_type === 'rating' && qAnalytics.distribution && (
                    <div className="ml-9">
                      <p className="text-2xl font-bold text-primary mb-2">
                        {qAnalytics.average} <span className="text-sm text-muted-foreground font-normal">/ 5 average</span>
                      </p>
                      <div className="flex items-end gap-2">
                        {[1, 2, 3, 4, 5].map((rating) => {
                          const count = qAnalytics.distribution[rating] || 0;
                          const percentage = qAnalytics.total > 0 ? (count / qAnalytics.total) * 100 : 0;
                          return (
                            <div key={rating} className="text-center">
                              <div 
                                className="w-12 bg-primary rounded-t"
                                style={{ height: `${Math.max(percentage, 4)}px` }}
                              ></div>
                              <span className="text-xs text-muted-foreground">{rating}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {question.question_type === 'open_ended' && qAnalytics.responses && (
                    <div className="ml-9">
                      <p className="text-sm text-muted-foreground mb-2">
                        {qAnalytics.total} text responses received
                      </p>
                      <div className="space-y-2">
                        {qAnalytics.responses.slice(0, 3).map((resp, idx) => (
                          <blockquote key={idx} className="border-l-2 border-primary pl-4 text-sm italic">
                            "{resp.text}"
                            <span className="text-xs text-muted-foreground not-italic ml-2">- {resp.state}</span>
                          </blockquote>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8 border-t">
          <p className="text-sm text-muted-foreground">
            This report contains citizen sentiment data collected through the Citizen Voice Nigeria platform.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Data is aggregated and anonymized. This is not election data.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
              <span className="text-white font-bold text-xs">CV</span>
            </div>
            <span className="font-semibold text-sm">Citizen Voice Nigeria</span>
          </div>
        </div>
      </main>
    </div>
  );
}
